import { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

// Custom CSS to hide time slots and show only all-day events
const calendarStyles = `
  .rbc-time-view .rbc-time-content {
    display: none;
  }
  .rbc-time-view .rbc-allday-cell {
    min-height: 400px;
    max-height: none;
  }
  .rbc-time-view .rbc-time-header-content {
    min-height: 400px;
  }
  .rbc-time-view .rbc-time-header-content .rbc-allday-cell {
    flex: 1;
  }
  .rbc-time-view .rbc-row-content {
    min-height: 100%;
  }
  .rbc-time-header-gutter {
    display: none;
  }
  .rbc-time-view .rbc-header {
    border-bottom: 1px solid #e2e8f0;
    padding: 8px 4px;
  }
  .rbc-event {
    padding: 4px 8px !important;
    margin-bottom: 2px;
  }
  .rbc-toolbar button {
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 2px;
  }
  .rbc-toolbar button.rbc-active {
    background-color: #475569;
    color: white;
  }
`;

function CalendarPage() {

  document.title = 'Calendar';

  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [kanbanJobs, setKanbanJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchJobsAndSchedule = async () => {
      if (!user) return;

      setLoading(true);
      try {
        // Fetch jobs with produced_quantity, shipped_quantity, and inventory_quantity
        const { data: jobs, error: jobsError } = await supabase
          .from('jobs')
          .select(`
            id, po_number, part_number, description, expected_delivery_date,
            estimated_running_days, shop_id, quantity_on_order,
            produced_quantity, shipped_quantity, shops(id, name),
            parts!inner(part_number, inventory_quantity)
          `);
        if (jobsError) throw jobsError;

        // Fetch part-machine mappings
        const { data: mappings, error: mappingsError } = await supabase
          .from('part_machine_mapping')
          .select('part_number, machine_id, machines(name, shop_id)');
        if (mappingsError) throw mappingsError;

        const partMachineMap = mappings.reduce((acc, m) => {
          acc[m.part_number] = { machine_id: m.machine_id, machine_name: m.machines.name };
          return acc;
        }, {});

        // Separate jobs into scheduled events (with due dates) and KANBAN jobs (without due dates)
        const scheduledEvents = [];
        const kanbanJobsList = [];

        jobs.forEach(job => {
          const machine = partMachineMap[job.part_number] || { machine_id: null, machine_name: 'Unassigned' };
          if (job.expected_delivery_date) {
            // Jobs with a due date: Show only on the due date
            const dueDate = new Date(job.expected_delivery_date);
            scheduledEvents.push({
              id: job.id,
              title: `${job.po_number} - ${job.part_number} (${job.description}) - ${machine.machine_name}`,
              start: dueDate,
              end: dueDate,
              allDay: true,
              resource: {
                shop: job.shops?.name || 'Unknown',
                shop_id: job.shop_id,
                machine_id: machine.machine_id,
                po_number: job.po_number,
                part_number: job.part_number,
                description: job.description,
                quantity_on_order: job.quantity_on_order,
                produced_quantity: job.produced_quantity,
                shipped_quantity: job.shipped_quantity,
                inventory: job.parts?.inventory_quantity || 0,
              },
            });
          } else {
            // Jobs without a due date (KANBAN): Add to KANBAN list
            kanbanJobsList.push({
              id: job.id,
              po_number: job.po_number,
              part_number: job.part_number,
              description: job.description,
              shop: job.shops?.name || 'Unknown',
              machine: machine.machine_name,
              quantity_on_order: job.quantity_on_order,
              produced_quantity: job.produced_quantity,
              shipped_quantity: job.shipped_quantity,
              inventory: job.parts?.inventory_quantity || 0,
            });
          }
        });

        setEvents(scheduledEvents);
        setKanbanJobs(kanbanJobsList);
      } catch (err) {
        console.error('Error fetching and scheduling jobs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobsAndSchedule();
  }, [user]);

  const handleEventClick = (event) => {
    setSelectedJob(event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
  };

  const navigateToJobPage = () => {
    if (selectedJob) {
      navigate(`/jobs/${selectedJob.resource.po_number}`);
      closeModal();
    }
  };

  const calculateProgressPercentage = (produced, inventory, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 0;
    const totalAvailable = (produced || 0) + (inventory || 0);
    return Math.min((totalAvailable / quantityOnOrder) * 100, 100);
  };

  const calculateShippedPercentage = (shipped, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 0;
    return Math.min((shipped / quantityOnOrder) * 100, 100);
  };

  const getJobStatus = (produced, shipped, ordered) => {
    if (shipped >= ordered && ordered > 0) return 'complete';
    if (shipped > 0) return 'partial';
    if (produced > 0) return 'in_production';
    return 'not_started';
  };

  const statusColors = {
    not_started: { bg: '#94a3b8', label: 'Not Started' },      // slate-400
    in_production: { bg: '#475569', label: 'In Production' },  // slate-600
    partial: { bg: '#d97706', label: 'Partially Shipped' },    // amber-600
    complete: { bg: '#059669', label: 'Complete' },            // emerald-600
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{calendarStyles}</style>
      <div className="p-4">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Job Scheduling Calendar</h1>
            <p className="text-xs text-slate-500 mt-0.5">View scheduled jobs and due dates</p>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4">
            {Object.entries(statusColors).map(([key, { bg, label }]) => (
              <div key={key} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: bg }}></div>
                <span className="text-xs text-slate-600">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar Card */}
        <div className="bg-white border border-slate-200 rounded-sm mb-4">
          <div className="p-4" style={{ height: '600px' }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              defaultView="month"
              views={['month', 'week', 'day']}
              style={{ height: '100%' }}
              eventPropGetter={(event) => {
                const status = getJobStatus(
                  event.resource.produced_quantity,
                  event.resource.shipped_quantity,
                  event.resource.quantity_on_order
                );
                return {
                  style: {
                    backgroundColor: statusColors[status].bg,
                    color: 'white',
                    borderRadius: '2px',
                    border: 'none',
                    padding: '2px 4px',
                    fontSize: '11px',
                  },
                };
              }}
              tooltipAccessor={(event) => {
                const status = getJobStatus(
                  event.resource.produced_quantity,
                  event.resource.shipped_quantity,
                  event.resource.quantity_on_order
                );
                return `${event.title} - ${statusColors[status].label}`;
              }}
              onSelectEvent={handleEventClick}
            />
          </div>
        </div>

        {/* KANBAN Jobs Section */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Kanban Jobs</h2>
            <span className="text-xs text-slate-500">{kanbanJobs.length} jobs without due dates</span>
          </div>
          {kanbanJobs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">PO Number</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Part Number</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Description</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Qty Ordered</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Produced</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Shipped</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Inventory</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Shop</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Machine</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {kanbanJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 font-medium">{job.po_number}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{job.part_number}</td>
                      <td className="px-3 py-2 text-sm text-slate-600 truncate max-w-xs" title={job.description}>{job.description}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{job.quantity_on_order}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{job.produced_quantity}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{job.shipped_quantity}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{job.inventory}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{job.shop}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{job.machine}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-3 py-4 text-sm text-slate-500">No KANBAN jobs found.</p>
          )}
        </div>

        {/* Job Details Modal */}
        <Transition appear show={isModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title className="text-sm font-semibold text-slate-800">
                        Job Details
                      </Dialog.Title>
                      <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">×</button>
                    </div>
                    {selectedJob && (
                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-slate-500">PO Number</p>
                            <p className="text-sm font-medium text-slate-800">{selectedJob.resource.po_number}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Part Number</p>
                            <p className="text-sm font-medium text-slate-800">{selectedJob.resource.part_number}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-slate-500">Description</p>
                            <p className="text-sm text-slate-700">{selectedJob.resource.description}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Due Date</p>
                            <p className="text-sm text-slate-700 tabular-nums">{moment(selectedJob.start).format('MM/DD/YYYY')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Shop</p>
                            <p className="text-sm text-slate-700">{selectedJob.resource.shop}</p>
                          </div>
                        </div>

                        <div className="border-t border-slate-100 pt-3">
                          <div className="grid grid-cols-4 gap-3 text-center">
                            <div>
                              <p className="text-xs text-slate-500">Ordered</p>
                              <p className="text-sm font-semibold text-slate-800 tabular-nums">{selectedJob.resource.quantity_on_order}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Produced</p>
                              <p className="text-sm font-semibold text-slate-800 tabular-nums">{selectedJob.resource.produced_quantity}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Shipped</p>
                              <p className="text-sm font-semibold text-slate-800 tabular-nums">{selectedJob.resource.shipped_quantity}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Inventory</p>
                              <p className="text-sm font-semibold text-slate-800 tabular-nums">{selectedJob.resource.inventory}</p>
                            </div>
                          </div>
                        </div>

                        <div className="border-t border-slate-100 pt-3 space-y-2">
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-slate-500">Production Progress</span>
                              <span className="text-slate-700 font-medium tabular-nums">
                                {calculateProgressPercentage(selectedJob.resource.produced_quantity, selectedJob.resource.inventory, selectedJob.resource.quantity_on_order).toFixed(0)}%
                              </span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-sm h-1.5">
                              <div
                                className="bg-slate-600 h-1.5 rounded-sm transition-all"
                                style={{ width: `${calculateProgressPercentage(selectedJob.resource.produced_quantity, selectedJob.resource.inventory, selectedJob.resource.quantity_on_order)}%` }}
                              ></div>
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-slate-500">Shipped</span>
                              <span className="text-slate-700 font-medium tabular-nums">
                                {calculateShippedPercentage(selectedJob.resource.shipped_quantity, selectedJob.resource.quantity_on_order).toFixed(0)}%
                              </span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-sm h-1.5">
                              <div
                                className="bg-emerald-600 h-1.5 rounded-sm transition-all"
                                style={{ width: `${calculateShippedPercentage(selectedJob.resource.shipped_quantity, selectedJob.resource.quantity_on_order)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                      <button
                        type="button"
                        onClick={closeModal}
                        className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        onClick={navigateToJobPage}
                        className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                      >
                        View Job Page
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
}

export default CalendarPage;
